import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { ProgressBar } from "../../design-system/ProgressBar";
import { Icon, type IconName } from "../../design-system/Icon";
import { Sparkline } from "../../components/Sparkline";
import { LoadingState, ErrorState } from "../../components/States";
import { healthTone, healthLabel, priorityTone, priorityLabel } from "../../components/health";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { useDateRange } from "../../components/DateRangeContext";
import { formatRange } from "../../lib/date-range";
import { CopyDataButton } from "../../components/CopyDataButton";
import type { PeriodRange } from "../../ipc/types";
import type { ExecutiveOverviewDto, RecommendationDto } from "../../ipc/types";
import { toCsv, suggestCsvName } from "../../lib/csv";
import { strings } from "../../i18n/es-CL";

/** Nombre del motor (como lo reporta The Brain) → ruta de su pantalla, para el botón "Revisar". */
const ENGINE_ROUTE: Record<string, string> = {
  Producto: "/producto", Marketplace: "/marketplace", Inventario: "/inventario", Ventas: "/ventas",
  Precios: "/precios", Compras: "/compras", Proveedores: "/proveedores", "Logística": "/logistica",
  Clientes: "/clientes", Analytics: "/analytics", Finanzas: "/finanzas", IA: "/ia",
};
import { ScoreGauge } from "./ScoreGauge";
import "./dashboard.css";

/** Consejo de la IA basado en el snapshot real del negocio (Fase 4 Item 5). */
function AiAdvisorCard(): JSX.Element {
  const client = useIpc();
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = async (): Promise<void> => {
    setBusy(true);
    const res = await client.getAiRecommendation();
    setBusy(false);
    setAnswer(res.ok ? res.value.answer : res.error.message);
  };
  return (
    <Card>
      <CardHeader
        title={<span className="row" style={{ gap: 8 }}><Icon name="spark" size={16} /> Consejo de la IA</span>}
        subtitle="Recomendación accionable desde tus datos reales"
        action={<Button size="sm" variant="secondary" icon="spark" disabled={busy} onClick={ask}>{busy ? "Pensando…" : "Pedir consejo"}</Button>}
      />
      <CardBody>
        {answer
          ? <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{answer}</div>
          : <div className="muted" style={{ fontSize: 13.5 }}>Pulsa “Pedir consejo” para que la IA analice tu negocio (ventas, stock, finanzas) y sugiera la acción más importante de hoy.</div>}
      </CardBody>
    </Card>
  );
}

const RECO_ICON: Record<RecommendationDto["kind"], IconName> = {
  opportunity: "spark",
  threat: "shield",
  optimization: "bolt",
};

function engineDotColor(score: number): string {
  if (score >= 90) return "var(--success)";
  if (score >= 80) return "var(--warning)";
  return "var(--danger)";
}

export function ExecutiveDashboard(): JSX.Element {
  const { range, previous, compare } = useDateRange();
  const periodRange: PeriodRange = {
    fromEpoch: range.fromEpoch, toEpoch: range.toEpoch,
    prevFromEpoch: compare ? previous.fromEpoch : 0,
    prevToEpoch: compare ? previous.toEpoch : 0,
  };
  const { state, reload } = useIpcQuery<ExecutiveOverviewDto>(
    (c) => c.getExecutiveOverview(periodRange),
    [range.fromEpoch, range.toEpoch, previous.fromEpoch, previous.toEpoch, compare],
  );
  const client = useIpc();
  const navigate = useNavigate();
  const decisionsRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);

  // "Revisar decisiones" (hero): desplaza a la tarjeta de decisiones.
  const scrollToDecisions = (): void => decisionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  // "Aprobar": marca la decisión como vista y la oculta (reaparece si la condición sigue vigente al recargar).
  const approve = (id: string): void => setDismissed((prev) => new Set(prev).add(id));
  // "Revisar" (por decisión): abre el módulo relacionado.
  const review = (engine: string): void => { const path = ENGINE_ROUTE[engine]; if (path) navigate(path); };

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const d = state.data;
  const last = d.revenueTrend[d.revenueTrend.length - 1] ?? 0;
  const visibleDecisions = d.decisions.filter((dec) => !dismissed.has(dec.id));

  // "Informe ejecutivo": exporta KPIs + salud por motor + decisiones a un CSV.
  const exportReport = async (): Promise<void> => {
    setExporting(true); setExportMsg(null);
    const rows: string[][] = [
      ...d.kpis.map((k) => ["KPI", k.label, k.value]),
      ...d.engines.map((e) => ["Motor", e.name, `${e.status} · ${e.score}/100`]),
      ...d.decisions.map((dec) => ["Decisión", dec.title, `${priorityLabel(dec.priority)} · ${dec.confidence}%`]),
      ...d.recommendations.map((r) => ["Recomendación", r.title, r.impact]),
    ];
    const csv = toCsv(["Sección", "Detalle", "Valor"], rows);
    const res = await client.saveExport(suggestCsvName("informe-ejecutivo"), csv);
    setExporting(false);
    if (!res.ok) { if (res.error.code !== "cancelled") setExportMsg(res.error.message); return; }
    setExportMsg(`Informe ejecutivo exportado a: ${res.value.path}`);
  };

  return (
    <div>
      {/* HERO — El Cerebro */}
      <section className="hero">
        <div className="hero__grid">
          <div>
            <div className="hero__eyebrow">
              <Icon name="brain" size={16} /> The Brain · CEO digital
            </div>
            <h1 className="hero__title">{strings.dashboard.greeting}</h1>
            <p className="hero__headline">{d.headline}</p>
            {d.period ? (
              <div className="row" style={{ gap: 8, margin: "8px 0" }}>
                <Badge tone="brand" dot>{formatRange({ fromEpoch: d.period.fromEpoch, toEpoch: d.period.toEpoch })}</Badge>
                {compare ? <span className="faint" style={{ fontSize: 12 }}>ventas y órdenes del período · vs período anterior</span> : null}
              </div>
            ) : null}
            <div className="hero__actions">
              <Button icon="check" onClick={scrollToDecisions}>Revisar decisiones</Button>
              <Button variant="secondary" icon="analytics" disabled={exporting} onClick={exportReport}>
                {exporting ? "Exportando…" : "Informe ejecutivo"}
              </Button>
              <CopyDataButton size="md" data={{
                title: "Estado del negocio",
                columns: ["Motor", "Salud", "Score", ""],
                rows: d.engines.map((e) => ({ primary: e.name, secondary: healthLabel(e.status), status: { label: `${e.score}` }, amount: "" })),
                metrics: d.kpis.map((k) => ({ label: k.label, value: k.value, ...(typeof k.deltaPct === "number" && k.deltaPct !== 0 ? { deltaPct: k.deltaPct } : {}) })),
                ...(d.period ? { periodLabel: formatRange(d.period) } : {}),
              }} />
            </div>
            {exportMsg ? <div className="callout" style={{ marginTop: "var(--sp-3)" }}><div className="bar" /><div className="body">{exportMsg}</div></div> : null}
          </div>
          <ScoreGauge score={d.overallScore} label={strings.dashboard.systemHealth} />
        </div>
      </section>

      {/* KPIs */}
      <div className="kpis">
        {d.kpis.map((k) => (
          <StatCard
            key={k.key}
            label={k.label}
            value={k.value}
            icon={k.icon as IconName}
            accent={k.accent}
            deltaPct={k.deltaPct}
          />
        ))}
      </div>

      {/* Columna principal + lateral */}
      <div className="dash-cols">
        <div className="grid">
          {/* Tendencia */}
          <Card>
            <CardHeader
              title={strings.dashboard.revenue}
              subtitle="Últimos 13 periodos · millones CLP"
              action={<Badge tone="brand" dot>En vivo</Badge>}
            />
            <CardBody>
              <Sparkline data={d.revenueTrend} ariaLabel={strings.dashboard.revenue} />
              <div className="trend-foot">
                <span className="trend-big tnum">${last}M</span>
                <Badge tone="success" dot>
                  +{d.kpis[0]?.deltaPct.toFixed(1)}% vs. periodo anterior
                </Badge>
              </div>
            </CardBody>
          </Card>

          {/* Decisiones pendientes */}
          <Card ref={decisionsRef}>
            <CardHeader
              title={strings.dashboard.pendingDecisions}
              subtitle={`${visibleDecisions.length} requieren tu aprobación`}
            />
            <CardBody>
              {visibleDecisions.length === 0 ? (
                <div className="faint" style={{ fontSize: 13, padding: "var(--sp-2) 0" }}>No hay decisiones pendientes. Todo en orden por ahora — a medida que operes, aquí aparecerán acciones prioritarias.</div>
              ) : null}
              {visibleDecisions.map((dec) => (
                <div className="decision" key={dec.id}>
                  <div className="decision__body">
                    <div className="decision__title">{dec.title}</div>
                    <div className="decision__why">{dec.rationale}</div>
                    <div className="decision__meta">
                      <Badge tone={priorityTone(dec.priority)} dot>
                        {priorityLabel(dec.priority)}
                      </Badge>
                      <span className="muted" style={{ fontSize: 12 }}>{dec.engine}</span>
                      <span className="decision__conf">
                        <b>{dec.confidence}%</b> {strings.dashboard.confidence}
                      </span>
                    </div>
                  </div>
                  <div className="decision__actions">
                    <Button size="sm" icon="check" onClick={() => approve(dec.id)}>{strings.dashboard.approve}</Button>
                    <Button size="sm" variant="ghost" disabled={!ENGINE_ROUTE[dec.engine]} onClick={() => review(dec.engine)}>{strings.dashboard.review}</Button>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>

        <div className="grid">
          {/* Consejo de la IA (datos reales del negocio) */}
          <AiAdvisorCard />

          {/* Recomendaciones */}
          <Card>
            <CardHeader title={strings.dashboard.recommendations} />
            <CardBody>
              {d.recommendations.length === 0 ? (
                <div className="faint" style={{ fontSize: 13, padding: "var(--sp-2) 0" }}>Aún no hay recomendaciones. Se generan cuando hay oportunidades o riesgos en tus datos (publicaciones pausadas, quiebres de stock, etc.).</div>
              ) : null}
              {d.recommendations.map((r) => (
                <div className="reco" key={r.id}>
                  <div className={`reco__ic reco__ic--${r.kind}`}>
                    <Icon name={RECO_ICON[r.kind]} size={17} />
                  </div>
                  <div>
                    <div className="reco__title">{r.title}</div>
                    <div className="reco__impact">{r.impact}</div>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          {/* Metas */}
          <Card>
            <CardHeader title={strings.dashboard.goals} />
            <CardBody>
              {d.goals.length === 0 ? (
                <div className="faint" style={{ fontSize: 13, padding: "var(--sp-2) 0" }}>Aún no hay metas. Se arman desde tus indicadores de Analytics cuando tengas actividad de ventas e inventario.</div>
              ) : null}
              {d.goals.map((g) => (
                <div className="goal" key={g.id}>
                  <div className="goal__row">
                    <span className="goal__label">{g.label}</span>
                    <span className="goal__val tnum">
                      {g.progress}% · {g.target}
                    </span>
                  </div>
                  <ProgressBar value={g.progress} ariaLabel={g.label} />
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Motores */}
      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader
          title={strings.dashboard.engines}
          subtitle="Salud por motor del ecosistema"
          action={<Badge tone={healthTone(d.status)} dot>{healthLabel(d.status)}</Badge>}
        />
        <CardBody>
          <div className="enginegrid">
            {d.engines.map((e) => (
              <div className="engine-mini" key={e.id}>
                <span className="engine-mini__dot" style={{ background: engineDotColor(e.score) }} />
                <div style={{ flex: 1 }}>
                  <div className="engine-mini__name">{e.name}</div>
                  <div className="engine-mini__score tnum">
                    {e.score}/100 · {e.uptimePct}% uptime
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
